import type React from 'react';
import { useEffect, useState } from 'react';
import type { FormEventHandler } from 'react';
import {
  Accordion,
  Alert,
  Box,
  Button,
  Divider,
  Form,
  FormActions,
  FormField,
  Heading,
  Icon,
  Input,
  Label,
  Modal,
  Paragraph,
  Text,
  TextButton,
} from '@zillow/constellation-10';
import Image from 'next/image';
import { useRouter } from 'next/router';
import FlexibleLandlordIcon from '../../../assets/FlexibleLandlord.svg';
import EmailSentIcon from '../../../assets/EmailSent.svg';
import { IconDollarSignCircleFilled, IconLockClosedFilled, IconSendFilled } from '@zillow/constellation-icons';
import { RentCreditReportWebApiHandler_sendRequestForZillowPayments } from '@zg-rentals/ts-rent-credit-report-client';
import type { ClickstreamTriggerObject } from '../../../analytics/events';
import {
  type ClickstreamAuthTriggerSource,
  emitAuthStartEvent,
  emitRequestPaymentsModalOpenEvent,
  emitRequestPaymentsSubmitEvent,
} from '../../../analytics/events';
import { useRentReportingAuth } from '../../../contexts/RentReportingAuthProvider';

interface BenefitSectionProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

function BenefitSection({ icon, title, description }: BenefitSectionProps) {
  return (
    <Box
      css={{ display: 'flex', alignItems: 'flex-start', marginBottom: '{spacing.looser}', gap: '{spacing.default}' }}
    >
      <Icon size="md">{icon}</Icon>
      <Box css={{ display: 'flex', flexDirection: 'column', gap: '{spacing.tighter}' }}>
        <Text textStyle="body-bold">{title}</Text>
        <Paragraph>{description}</Paragraph>
      </Box>
    </Box>
  );
}

function BenefitsAccordion() {
  const benefits = [
    {
      icon: <IconDollarSignCircleFilled />,
      title: 'More ways to pay',
      description:
        'Freedom from checks means you can pay with your debit/credit card, or direct debit from your bank account.',
    },
    {
      icon: <IconSendFilled />,
      title: 'Pay from anywhere',
      description: "Pay on the go, or schedule your payments on time, it's up to you.",
    },
    {
      icon: <IconLockClosedFilled />,
      title: 'Your security is our priority',
      description: 'We partner with payment-security experts and never sell your information.',
    },
  ];

  return (
    <Accordion.Root>
      <Accordion.Item value="benefits">
        <Accordion.Header>
          <Text textStyle="body">The benefits of paying with Zillow</Text>
        </Accordion.Header>
        <Accordion.Panel>
          <Box css={{ padding: '16px 0' }}>
            {benefits.map((benefit, index) => (
              <BenefitSection key={index} icon={benefit.icon} title={benefit.title} description={benefit.description} />
            ))}
          </Box>
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion.Root>
  );
}

export default function RequestZillowPaymentsModal({
  ctaText,
  clickstreamTriggerObject,
  authTriggerSource,
}: {
  ctaText: string;
  clickstreamTriggerObject: ClickstreamTriggerObject;
  authTriggerSource: ClickstreamAuthTriggerSource;
}) {
  const router = useRouter();
  const { user, login } = useRentReportingAuth();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [emailSent, setEmailSent] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');
  const [emailSendError, setEmailSendError] = useState<boolean>(false);
  const [emailSending, setEmailSending] = useState<boolean>(false);

  // Auto-open modal after login if query param is present
  useEffect(() => {
    if (router.query.open_zrm_modal === clickstreamTriggerObject && user?.isLoggedIn) {
      emitRequestPaymentsModalOpenEvent(clickstreamTriggerObject);
      setIsOpen(true);

      // Clean up query param
      const { open_zrm_modal: _open_zrm_modal, ...restQuery } = router.query;
      router.replace(
        {
          pathname: router.pathname,
          query: restQuery,
        },
        undefined,
        { shallow: true },
      );
    }
  }, [router.query, user, clickstreamTriggerObject, router.pathname, router]);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
  };

  const handleOpenModal = () => {
    if (!user?.isLoggedIn) {
      // User not logged in - emit auth start event, add modal trigger to URL, then redirect to login
      emitAuthStartEvent(authTriggerSource);
      if (typeof window !== 'undefined') {
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.set('open_zrm_modal', clickstreamTriggerObject);
        window.history.replaceState({}, '', currentUrl.toString());
      }
      login(authTriggerSource);
      return;
    }

    // User is logged in - open modal normally
    emitRequestPaymentsModalOpenEvent(clickstreamTriggerObject);
    setIsOpen(true);
  };

  const resetEmail = () => {
    setEmail('');
    setEmailSent(false);
  };

  const onSendEmailClicked: FormEventHandler = (event) => {
    event.preventDefault();
    emitRequestPaymentsSubmitEvent(clickstreamTriggerObject);
    setEmailSending(true);
    return RentCreditReportWebApiHandler_sendRequestForZillowPayments({ email })
      .then(() => {
        setEmailSent(true);
        setEmailSendError(false);
        setEmailSending(false);
        return;
      })
      .catch(() => {
        setEmailSendError(true);
        setEmailSending(false);
      });
  };

  const requestPaymentsBody = (
    <>
      {emailSendError && (
        <Alert tone="critical" css={{ marginBottom: '{spacing.looser}' }}>
          <Alert.Body>
            <Paragraph data-testid="error-message">
              We were unable to send your request for Zillow payments. Please try again.
            </Paragraph>
          </Alert.Body>
        </Alert>
      )}
      <Box
        css={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'left',
          marginRight: '16px',
          flexShrink: 0,
          marginBottom: '16px',
        }}
      >
        <Image src={FlexibleLandlordIcon} alt="Flexible landlord" width={120} height={120} />
      </Box>

      <Heading level={5} css={{ marginBottom: '16px' }}>
        Ask your landlord to set up Zillow payments
      </Heading>

      <Paragraph css={{ marginBottom: '16px' }}>
        It&apos;s easy. We&apos;ll send an email to your landlord, explaining the benefits of Zillow payments and how to
        set up your rental property.
      </Paragraph>

      <Paragraph css={{ marginBottom: '24px' }}>
        If they choose to collect rent with Zillow, you&apos;ll get access to rent reporting for free.
      </Paragraph>

      <Box css={{ marginBottom: '24px' }}>
        <FormField
          label={<Label>Landlord&apos;s email</Label>}
          control={<Input type="email" onChange={(e) => setEmail(e.target.value)} value={email} />}
        />
      </Box>
      <BenefitsAccordion />
    </>
  );

  const emailSentBody = (
    <Box>
      <Box css={{ marginBottom: '{spacing.default}' }}>
        <Image src={EmailSentIcon} alt="Flexible landlord" width={120} height={120} />
      </Box>
      <Heading level={4} css={{ marginBottom: '{spacing.default}' }} data-testid="email-sent-heading">
        Your request for Zillow payments was sent
      </Heading>
      <Paragraph css={{ marginBottom: '{spacing.looser}' }}>
        Once your landlord sets up payments, we&apos;ll follow up with you to get started.
      </Paragraph>
      <Paragraph textStyle="body" css={{ color: 'text.subtle', marginBottom: '{spacing.tightest}' }}>
        Your landlord
      </Paragraph>
      <Paragraph textStyle="body" css={{ marginBottom: '{spacing.looser}' }}>
        {email}
      </Paragraph>
      <Paragraph css={{ marginBottom: '{spacing.looser}' }}>
        If you&apos;d like to send another request for Zillow payments,{' '}
        <TextButton textStyle="body-bold" onClick={resetEmail} data-testid="reset-form-button">
          click here
        </TextButton>
        .
      </Paragraph>
      <Paragraph css={{ marginBottom: '{spacing.looser}' }}>
        <strong>Change your mind?</strong> You can sign up for CreditClimb at any time from the &quot;Your rental&quot;
        page.
      </Paragraph>
    </Box>
  );

  const modalBody = emailSent ? emailSentBody : requestPaymentsBody;

  return (
    <>
      <TextButton textStyle="body-bold" data-testid="request-zillow-payments-button" onClick={handleOpenModal}>
        {ctaText}
      </TextButton>

      <Modal.Root open={isOpen} onOpenChange={handleOpenChange} size={{ base: 'fullScreen', md: 'md' }}>
        <Modal.Portal>
          <Modal.Backdrop data-panda-theme="zillow">
            <Modal.Content asChild>
              <Form onSubmit={onSendEmailClicked}>
                <Modal.Header css={{ height: '56px' }}>
                  <Modal.CloseButton />
                </Modal.Header>
                <Divider />
                <Modal.Body css={{ padding: { base: '{spacing.default}', md: '{spacing.looser}' } }}>
                  {modalBody}
                </Modal.Body>
                <Divider />
                <Modal.Footer>
                  {emailSent ? (
                    <Button
                      tone="brand"
                      emphasis="filled"
                      onClick={() => {
                        handleOpenChange(false);
                      }}
                    >
                      Okay
                    </Button>
                  ) : (
                    <FormActions css={{ marginBlockStart: 'auto !important' }}>
                      <Button
                        loading={emailSending}
                        tone="brand"
                        emphasis="filled"
                        disabled={!email}
                        type="submit"
                        data-testid="send-request-to-landlord-button"
                      >
                        Send request to landlord
                      </Button>
                    </FormActions>
                  )}
                </Modal.Footer>
              </Form>
            </Modal.Content>
          </Modal.Backdrop>
        </Modal.Portal>
      </Modal.Root>
    </>
  );
}
